/*
 * The purpose of this file is to alleviate numerous issues around displaying
 *  modals and other elements which require the scrolling of the page to be
 *  disabled while they are active (Problem 1). We will call this state
 *  "locked".
 *
 * -- Problem 1: How do you prevent scrolling?
 *
 * The most effective way to prevent scrolling is to set html or body to
 *  position: fixed 0 0 0 0. However, there is one drawback to this method:
 *  when these styles are applied, the page will scroll to the top (Problem 2)
 *  and the scroll position of the browser will return 0 (Problem 3).
 *
 *  Also to note, the overflow: hidden property is ignored by iOS Safari and
 *   thus cannot be relied upon.
 *
 * -- Problem 2: How do you prevent the page from scrolling to the top?
 *
 * This problem can be solved one of two ways, and the side-effects of each
 *  method are not completely understood.
 *
 *    Method A: Apply a negative value to html top, e.g. top: -400px
 *    Method B: Apply a transform to the site content and footer, e.g.
 *               transform: translateY(-400px)
 *
 * Note that with either method active, querying the scroll position of the
 *  page will still return 0.
 *
 * -- Problem 3: How do you return a non-zero scroll position?
 *
 * This problem can be solved by creating a shim between our code and the
 *  browser. When locked screen is engaged, the scroll position becomes
 *  "locked", meaning that whil the browser says the value is 0, our function
 *  says that it's 400px.
 *
 * The one drawback of this approach is that third party modules will not
 *  recieve the benefits of this lock unless they are modified.
 *
 * -- Problem 4: How do you handle multiple page elements requiring a
 *                lock at the same time?
 *
 * This problem is solved by creating a stack where components needing a
 *  lock can register themselves. If the stack is empty, the lock
 *  is turned off. If the stack is filled, lock is turned on.
 *
 */

// ///////////////
// Dependencies //
// ///////////////

import * as pane from './pane'

// ////////////
// Variables //
// ////////////

// Elements

const html = document.documentElement
let moveWhenFixed

// State

let initialized

// /////////////////
// Initialization //
// /////////////////

export const init = () => {
  if (!initialized) {
    // try to find an element to move when locked
    moveWhenFixed = document.querySelector('.js--move-when-screen-is-locked')
    if (moveWhenFixed) initialized = true
  }
}

export const deinit = () => {
  initialized = undefined
  moveWhenFixed = undefined
}

// //////////////////
// Private Methods //
// //////////////////

const styleHtml = (position = '', top = '', right = '', bottom = '', left = '') => {
  html.style.position = position
  html.style.top = top
  html.style.right = right
  html.style.bottom = bottom
  html.style.left = left
}

const transformPage = (transform = '') => {
  moveWhenFixed.style.transform = transform
  moveWhenFixed.style['-webkit-transform'] = transform
}

// /////////////////
// Public Methods //
// /////////////////

export const lock = (x, y) => {
  init()

  if (initialized) {
    // make sure we're scrolled to the correct position
    pane.hardScroll(x, y)
    // position: fixed the html and
    styleHtml('fixed', '0', '0', '0', '0')
    // transform the page to the scrolled position
    transformPage(`translateX(-${x}px) translateY(-${y}px)`)
  }
}

export const unlock = (x, y) => {
  init()

  if (initialized) {
    transformPage() // clear page transform
    styleHtml() // remove position: fixed
    pane.hardScroll(x, y) // return window to position x, y
  }
}
